import { useRef, type ChangeEvent } from 'react';

export interface ImageUploadProps {
  /** Current image to preview (URL or data URL). */
  previewSrc?: string | null;
  /** Called with the chosen file and its base64 data URL. */
  onSelect: (file: File, dataUrl: string) => void;
  /** Called when the user clears the image. */
  onRemove?: () => void;
  accept?: string;
  /** Helper text under the control. */
  hint?: string;
  /** Size of the preview tile in pixels. */
  previewSize?: number;
}

/** Image picker with a preview tile and choose/remove buttons (no external deps). */
export function ImageUpload({
  previewSrc,
  onSelect,
  onRemove,
  accept = 'image/png,image/jpeg,image/svg+xml,image/webp',
  hint,
  previewSize = 64,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onSelect(file, reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  return (
    <div>
      <div className="d-flex align-items-center">
        <div
          className="border rounded d-inline-flex align-items-center justify-content-center bg-light overflow-hidden flex-shrink-0"
          style={{ width: previewSize, height: previewSize }}
        >
          {previewSrc ? (
            <img src={previewSrc} alt="preview" className="mw-100 mh-100" />
          ) : (
            <i className="ph-image text-muted fs-2" />
          )}
        </div>

        <div className="ms-3">
          <button type="button" className="btn btn-light" onClick={() => inputRef.current?.click()}>
            <i className="ph-upload-simple me-2" />
            Choose image
          </button>
          {previewSrc && onRemove && (
            <button type="button" className="btn btn-light text-danger ms-2" onClick={onRemove}>
              <i className="ph-trash me-2" />
              Remove
            </button>
          )}
          {hint && <div className="text-muted fs-sm mt-2">{hint}</div>}
        </div>
      </div>

      <input ref={inputRef} type="file" accept={accept} className="d-none" onChange={onChange} />
    </div>
  );
}
